import React from 'react';
import { X, ArrowRight, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import Button from '../ui/Button';
import {
  activeGroup,
  allAudienceRules,
  audienceRuleCount,
  groupedRules,
  needsColumnNote,
  ruleKey,
  ruleNeedsNoValue,
  RULE_OP_SYMBOL,
} from '../../services/offerStudioService';
import { opIsMulti, parseListValue } from '../segmentBuilder/criteriaUtils';
import { CriteriaSentence } from '../segmentBuilder/criteriaText';

function ruleValueLabel(rule) {
  // A stored rule can already carry a list; show its values, not the raw JSON.
  const base = rule.offer_value_label || (opIsMulti(rule.operator) ? parseListValue(rule.value).join(', ') : rule.value);
  return rule.operator === 'between' && rule.value2 ? `${base} – ${rule.value2}` : base;
}

/**
 * One row of the rules list. Several picks on the same attribute share a row and read as
 * "is any of", which is how they are combined in the segment.
 */
function RuleRow({ row, onRemove }) {
  const { rule } = row;
  const multi = row.rules.length > 1;
  const under = rule.offer_direction === 'under';

  if (rule.kind === 'group') {
    return (
      <span className="truncate">
        <span className="font-medium">{rule.display_name || rule.offer_value_label || rule.offer_value}</span>
        <span className="ml-1 text-[10px] uppercase text-gray-400" title={rule.offer_feature ? `${rule.offer_feature} = ${rule.offer_value}` : ''}>
          grouped
        </span>
      </span>
    );
  }

  return (
    <span className="min-w-0">
      <span className="text-gray-500">{rule.display_name}</span>{' '}
      <span className={under ? 'text-red-700' : 'text-gray-500'}>{RULE_OP_SYMBOL[row.operator] || row.operator}</span>
      {ruleNeedsNoValue(row.operator) ? null : (
        <span className="ml-1 inline-flex flex-wrap items-center gap-1">
          {row.rules.map((r) => (
            <span
              key={ruleKey(r)}
              className={`inline-flex items-center gap-1 font-medium ${
                multi ? 'rounded bg-white px-1.5 py-0.5 ring-1 ring-gray-200' : ''
              }`}
            >
              {ruleValueLabel(r)}
              {multi ? (
                <button
                  type="button"
                  onClick={() => onRemove(r)}
                  className="text-gray-400 hover:text-red-600"
                  aria-label={`Remove ${ruleValueLabel(r)} from ${rule.display_name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </span>
          ))}
        </span>
      )}
      {!rule.column ? (
        <span className="ml-1 text-[10px] uppercase text-amber-600" title="No exact profile column for this attribute; you'll pick one in the segment editor">
          pick column
        </span>
      ) : (
        <span className="ml-1 text-[10px] text-gray-400" title={`${rule.schema}.${rule.table}.${rule.column}`}>
          → {rule.column}
        </span>
      )}
    </span>
  );
}

/**
 * How big the audience these rules describe is. An empty result is the thing worth
 * catching here, so it is called out rather than shown as a quiet zero.
 */
function AudienceSize({ size, attributesNeedingColumn, autoCount, onToggleAutoCount }) {
  const { count, status, tookMs, error, canCount } = size;
  const running = status === 'running';
  const empty = count === 0 && status === 'fresh';

  const note = running
    ? 'Counting on Databricks…'
    : error
      ? error
      : attributesNeedingColumn.length
        ? needsColumnNote(attributesNeedingColumn)
        : status === 'stale'
          ? 'Rules changed — size is out of date.'
          : count == null
            ? 'Add a rule to see how many profiles match.'
            : empty
              ? 'No profiles match every rule. Try removing one.'
              : `distinct profiles${tookMs ? ` · ${(tookMs / 1000).toFixed(1)}s` : ''}`;

  return (
    <div className={`px-3 py-2 border-b ${empty ? 'bg-red-50' : ''}`}>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p
            className={`text-2xl font-semibold tabular-nums leading-none ${
              status === 'stale' ? 'text-gray-400' : empty ? 'text-red-700' : 'text-gray-900'
            }`}
            aria-live="polite"
          >
            {running && count == null ? <span className="text-gray-300">…</span> : count == null ? <span className="text-gray-300">—</span> : Number(count).toLocaleString()}
          </p>
          <p className={`mt-1 text-[11px] ${error || empty ? 'text-red-700' : 'text-gray-500'}`}>{note}</p>
        </div>
        <button
          type="button"
          onClick={size.run}
          disabled={!canCount || running}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded border px-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          title={canCount ? 'Count now' : 'Finish the rules to get a count'}
        >
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Size
        </button>
      </div>
      <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-500">
        <input type="checkbox" checked={autoCount} onChange={(e) => onToggleAutoCount(e.target.checked)} className="h-3 w-3 accent-[#0062FB]" />
        Count automatically as I add rules
      </label>
    </div>
  );
}

/**
 * Explore rules: the set of (feature = value) conditions the user has picked
 * from findings, with the size of the audience they describe and an English
 * rendering of it, both read from the criteria the segment editor would receive.
 */
function RuleList({ rules, onRemove }) {
  if (!rules.length) return null;
  return (
    <ul className="space-y-1">
      {groupedRules(rules).map((row) => (
        <li key={row.key} className="flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-sm">
          <RuleRow row={row} onRemove={onRemove} />
          <button
            type="button"
            onClick={() => row.rules.forEach((r) => onRemove(r))}
            className="p-0.5 text-gray-400 hover:text-red-600 shrink-0"
            aria-label={
              row.rules.length > 1
                ? `Remove all ${row.rule.display_name} rules`
                : `Remove rule ${row.rule.display_name}`
            }
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function RulesPanel({
  audience,
  match,
  onMatchChange,
  onRemove,
  onReset,
  onStartGroup,
  onActivateGroup,
  onRemoveGroup,
  onBuildSegment,
  presets,
  onAddPreset,
  isRuleActive,
  criteria = null,
  size = null,
  attributesNeedingColumn = [],
  autoCount = true,
  onToggleAutoCount,
}) {
  const rules = allAudienceRules(audience);
  const count = audienceRuleCount(audience);
  const multi = (audience?.groups || []).length > 1;
  const current = activeGroup(audience);
  const canStartGroup = (current?.rules || []).length > 0;

  return (
    <section className="rounded-lg border bg-white" aria-label="Rules">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs uppercase tracking-wide text-gray-500">{multi ? 'Audience' : 'Rules'}</span>
        <div className="inline-flex rounded border text-xs overflow-hidden" role="radiogroup" aria-label={multi ? 'How groups combine' : 'How different attributes combine'}>
          {[
            { m: 'ALL', title: multi ? 'A profile has to match every group' : 'A profile has to match every attribute below' },
            { m: 'ANY', title: multi ? 'A profile matching any one group qualifies' : 'A profile matching any one attribute below qualifies' },
          ].map(({ m, title }) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={match === m}
              onClick={() => onMatchChange(m)}
              title={title}
              className={`px-2 py-1 ${match === m ? 'bg-[#0062FB] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      {size && count ? (
        <AudienceSize
          size={size}
          attributesNeedingColumn={attributesNeedingColumn}
          autoCount={autoCount}
          onToggleAutoCount={onToggleAutoCount}
        />
      ) : null}
      <div className="p-3 space-y-2">
        {count === 0 ? (
          <p className="text-xs text-gray-500">
            Add rules to include or exclude people, then click &quot;Build Segment.&quot;
          </p>
        ) : multi ? (
          <div className="space-y-2">
            {(audience.groups || []).map((g, i) => {
              const selected = g.id === audience.activeGroupId;
              return (
                <div
                  key={g.id}
                  className={`rounded border px-2 py-1.5 ${selected ? 'ring-2 ring-[#0062FB] border-[#0062FB]' : 'border-gray-200'}`}
                >
                  <div className="mb-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onActivateGroup?.(g.id)}
                      className="flex min-w-0 flex-1 items-center justify-between text-left text-[11px] uppercase tracking-wide text-gray-500"
                    >
                      <span className="truncate">{g.name || `Group ${i + 1}`}</span>
                      {selected ? <span className="ml-1 shrink-0 normal-case text-[#0062FB]">adding here</span> : null}
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemoveGroup?.(g.id)}
                      className="shrink-0 p-0.5 text-gray-400 hover:text-red-600"
                      aria-label={`Remove ${g.name || `Group ${i + 1}`}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <RuleList rules={g.rules} onRemove={onRemove} />
                  {!g.rules.length ? <p className="text-xs text-gray-400">Add a finding to this group.</p> : null}
                </div>
              );
            })}
            {audience.globalExcludes?.length ? (
              <div className="rounded border border-red-100 bg-red-50/40 px-2 py-1.5">
                <div className="mb-1 text-[11px] uppercase tracking-wide text-red-700">Exclude from everyone</div>
                <RuleList rules={audience.globalExcludes} onRemove={onRemove} />
              </div>
            ) : null}
          </div>
        ) : (
          <RuleList rules={rules} onRemove={onRemove} />
        )}
        {canStartGroup ? (
          <button
            type="button"
            onClick={onStartGroup}
            className="text-xs text-[#0062FB] hover:underline"
          >
            + Start another group
          </button>
        ) : null}
        {criteria && count ? (
          <p className="border-t pt-2 text-xs leading-relaxed text-gray-600">
            <CriteriaSentence criteria={criteria} />
          </p>
        ) : null}
        <div className="flex items-center gap-2 pt-1">
          {onBuildSegment ? (
            <Button variant="outline" size="sm" className="flex-1" disabled={count === 0} onClick={onBuildSegment}>
              Build segment <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : null}
          <button type="button" onClick={onReset} disabled={count === 0} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-40" aria-label="Reset rules">
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
        {presets?.length ? (
          <div className="pt-2 border-t">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Start with</div>
            <div className="flex flex-wrap gap-1">
              {presets.map((p) => {
                const active = isRuleActive?.(p);
                return (
                  <button
                    key={`${p.feature}::${p.value}`}
                    type="button"
                    onClick={() => onAddPreset(p)}
                    disabled={active}
                    className={`rounded-full border px-2 py-0.5 text-xs ${active ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 text-gray-700 hover:border-[#0062FB] hover:text-[#0062FB]'}`}
                    title={p.text}
                  >
                    <span className="text-gray-500">{p.display_label}:</span> {p.value_label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
